/**
 * Hook for syncing Lightning transaction history from NWC wallet to Evolu.
 *
 * Fetches historical transactions via NWCClient.listTransactions() and
 * persists any that don't already exist in the local database. Deduplicates
 * by payment hash to avoid double-recording.
 *
 * Called once on mount when a connected NWC client is available, and can
 * be triggered manually via refresh().
 *
 * SECURITY: Never logs payment hashes, invoices, or preimages.
 *
 * Tier: 2 (Lightning features).
 *
 * @module lightning/hooks/useLightningHistorySync
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { NWCClient } from '@getalby/sdk';
import type { ConnectionStatus } from './useNwcClient';
import type { LightningTransactionDataSource } from './useLightningTransactions';
import type { LightningTransaction } from './useLightningHistory';
import { msatsToSats } from '@/lib/amount-conversion';

/** Maximum number of transactions to fetch per sync. */
const SYNC_LIMIT = 100;

/** Options for useLightningHistorySync. */
export interface UseLightningHistorySyncOptions {
  /** NWCClient instance, or null if not connected. */
  readonly client: NWCClient | null;
  /** Current connection status. */
  readonly connectionStatus: ConnectionStatus;
  /** Data source for persisting transactions to Evolu. */
  readonly dataSource: LightningTransactionDataSource;
  /** Existing transactions from Evolu (for deduplication). */
  readonly existingTransactions: readonly LightningTransaction[];
  /** Active wallet connection ID to tag synced transactions with. */
  readonly connectionId: string | null;
  /** Map from stringified transaction IDs to original Evolu branded IDs (for updates). */
  readonly txRowIdMap?: ReadonlyMap<string, unknown>;
}

/** Return type for useLightningHistorySync. */
export interface UseLightningHistorySyncResult {
  /** Whether a sync is currently in progress. */
  readonly isSyncing: boolean;
  /** Number of new transactions imported in the last sync. */
  readonly lastSyncCount: number;
  /** Error message from the last sync attempt, if any. */
  readonly syncError: string | null;
  /** Manually trigger a sync. */
  readonly refresh: () => void;
}

/**
 * Sync Lightning transaction history from NWC wallet to local Evolu storage.
 *
 * On first connect, fetches up to SYNC_LIMIT historical transactions and
 * saves any that aren't already in Evolu (deduplicates by payment hash).
 *
 * @param options - Client, status, data source, and existing transactions
 * @returns Sync state and manual refresh function
 */
export function useLightningHistorySync(
  options: UseLightningHistorySyncOptions,
): UseLightningHistorySyncResult {
  const { client, connectionStatus, dataSource, existingTransactions, connectionId, txRowIdMap } =
    options;
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncCount, setLastSyncCount] = useState(0);
  const [syncError, setSyncError] = useState<string | null>(null);
  const hasSyncedRef = useRef(false);

  const doSync = useCallback(async (): Promise<void> => {
    if (!client || connectionStatus !== 'connected') return;
    if (isSyncing) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      const response = await client.listTransactions({ limit: SYNC_LIMIT });

      // Build a set of existing payment hashes for deduplication
      const existingHashes = new Set<string>();
      for (const tx of existingTransactions) {
        if (tx.paymentHash) {
          existingHashes.add(tx.paymentHash);
        }
      }

      let importedCount = 0;

      for (const nwcTx of response.transactions) {
        // Skip if we already have this transaction
        if (nwcTx.payment_hash && existingHashes.has(nwcTx.payment_hash)) {
          continue;
        }

        // Skip pending transactions — only import settled or failed
        if (nwcTx.state !== 'settled' && nwcTx.state !== 'failed') {
          continue;
        }

        const direction = nwcTx.type === 'incoming' ? 'receive' : 'send';
        const amountSats = msatsToSats(nwcTx.amount);
        const feeSats = nwcTx.fees_paid ? msatsToSats(nwcTx.fees_paid) : 0;
        const settledAt = nwcTx.settled_at ? new Date(nwcTx.settled_at * 1000).toISOString() : null;
        const initiatedAt = nwcTx.created_at
          ? new Date(nwcTx.created_at * 1000).toISOString()
          : new Date().toISOString();

        // Extract Lightning Address from metadata if available
        const lightningAddress = nwcTx.metadata?.recipient_data?.identifier ?? null;

        dataSource.recordTransaction({
          type: 'lightning',
          direction,
          amountSats,
          feeSats,
          paymentHash: nwcTx.payment_hash || null,
          bolt11: nwcTx.invoice || null,
          preimage: nwcTx.preimage || null,
          lightningAddress,
          note: nwcTx.description || null,
          status: nwcTx.state === 'failed' ? 'failed' : 'settled',
          initiatedAt,
          settledAt,
          custodyType: 'self-custodial',
          connectionId,
        });

        importedCount++;
      }

      // Backfill: tag untagged transactions that appear in the NWC response.
      // Only tags transactions with null walletConnectionId (never synced or
      // from before connectionId tracking). Does NOT re-tag transactions that
      // already belong to a different connection — Alby Hub may return
      // transactions from all sub-wallets, and re-tagging would steal
      // transactions from their correct wallet.
      if (connectionId) {
        const nwcHashes = new Set(response.transactions.map((t) => t.payment_hash).filter(Boolean));
        const needsTagging = existingTransactions.filter(
          (tx) => tx.paymentHash && nwcHashes.has(tx.paymentHash) && tx.walletConnectionId === null,
        );
        for (const tx of needsTagging) {
          const evoluId = txRowIdMap?.get(tx.id);
          dataSource.tagTransaction(tx.id, connectionId, evoluId);
        }
      }

      setLastSyncCount(importedCount);
    } catch {
      setSyncError('Could not fetch transaction history from your Lightning wallet.');
    } finally {
      setIsSyncing(false);
    }
  }, [
    client,
    connectionStatus,
    dataSource,
    existingTransactions,
    isSyncing,
    connectionId,
    txRowIdMap,
  ]);

  const refresh = useCallback((): void => {
    void doSync();
  }, [doSync]);

  // Reset sync flag when switching wallets so the new wallet gets synced
  const prevConnectionIdRef = useRef(connectionId);
  useEffect(() => {
    if (connectionId !== prevConnectionIdRef.current) {
      prevConnectionIdRef.current = connectionId;
      hasSyncedRef.current = false;
    }
  }, [connectionId]);

  // Auto-sync once when client connects
  useEffect(() => {
    if (connectionStatus === 'connected' && client && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      void doSync();
    }
    // Reset sync flag on disconnect so reconnect triggers a fresh sync
    if (connectionStatus === 'disconnected') {
      hasSyncedRef.current = false;
    }
  }, [connectionStatus, client, doSync]);

  return { isSyncing, lastSyncCount, syncError, refresh };
}
